import React, { useState } from 'react';
import { Task } from '../types';
import { GitBranch, ArrowRight, AlertCircle, Plus, X, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { getPriorityColors, getStatusColors } from '../constants/colorScheme';
import {
  WIDGET_ALERT_INFO_CLASS,
  WIDGET_ALERT_INFO_TEXT_CLASS,
  WIDGET_ALERT_WARNING_CLASS,
  WIDGET_ALERT_WARNING_TEXT_CLASS,
  WIDGET_MUTED_TEXT_CLASS,
} from '../constants/widgetTheme';

interface DependenciesProps {
  task: Task;
  allTasks: Task[];
  onUpdateDependencies: (dependencies: string) => Promise<void>;
}

export default function Dependencies({ task, allTasks, onUpdateDependencies }: DependenciesProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const scopedTasks = allTasks.filter(
    (item) => item.projectId === task.projectId && item.organizationId === task.organizationId
  );

  // Parse current dependencies
  const dependencyIds = task.dependencies?.split(',').filter(Boolean) || [];
  const dependencyTasks = dependencyIds
    .map((id) => scopedTasks.find((t) => t.id === id.trim()))
    .filter(Boolean) as Task[];

  // Find tasks that depend on this task
  const blockedTasks = scopedTasks.filter((t) =>
    t.dependencies?.split(',').some((depId) => depId.trim() === task.id)
  );

  // Available tasks to add as dependencies (exclude self, current dependencies, and subtasks)
  const availableTasks = scopedTasks.filter(
    (t) =>
      t.id !== task.id &&
      !dependencyIds.includes(t.id) &&
      !(task.subtaskIds || []).includes(t.id) &&
      (searchTerm === '' ||
        t.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.id.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleStartEditing = () => {
    const scopedDependencyIds = dependencyIds.filter((id) =>
      scopedTasks.some((item) => item.id === id)
    );
    setSelectedIds(scopedDependencyIds);
    setIsEditing(true);
  };

  const handleAddDependency = (taskId: string) => {
    if (!selectedIds.includes(taskId)) {
      setSelectedIds([...selectedIds, taskId]);
    }
  };

  const handleRemoveDependency = (taskId: string) => {
    setSelectedIds(selectedIds.filter((id) => id !== taskId));
  };

  const handleSave = async () => {
    try {
      const allowedIds = new Set(scopedTasks.map((item) => item.id));
      const sanitizedIds = selectedIds.filter((id) => allowedIds.has(id));
      await onUpdateDependencies(sanitizedIds.join(','));
      setIsEditing(false);
      setSearchTerm('');
      toast.success('Dependencies updated');
    } catch (error) {
      toast.error('Failed to update dependencies');
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSearchTerm('');
    setSelectedIds([]);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <GitBranch className="w-5 h-5" />
            Dependencies
          </span>
          {!isEditing && (
            <Button size="sm" variant="outline" onClick={handleStartEditing}>
              <Plus className="w-4 h-4 mr-1" />
              Edit
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Blocking Tasks (this task depends on) */}
        <div>
          <h4 className="mb-2 flex items-center gap-1 text-sm font-semibold text-foreground">
            <AlertCircle className={`h-4 w-4 ${WIDGET_ALERT_WARNING_TEXT_CLASS}`} />
            Blocked By ({dependencyTasks.length})
          </h4>
          {dependencyTasks.length === 0 && !isEditing && (
            <p className={`text-sm italic ${WIDGET_MUTED_TEXT_CLASS}`}>No blocking dependencies</p>
          )}
          {!isEditing ? (
            <div className="space-y-2">
              {dependencyTasks.map((dep) => (
                <DependencyTaskCard key={dep.id} task={dep} direction="blocking" />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {/* Show selected dependencies */}
              {selectedIds.map((id) => {
                const depTask = allTasks.find((t) => t.id === id);
                if (!depTask) return null;
                return (
                  <div
                    key={id}
                    className={`${WIDGET_ALERT_WARNING_CLASS} flex items-center gap-2 rounded p-2`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{depTask.title}</div>
                      <div className={`text-xs ${WIDGET_MUTED_TEXT_CLASS}`}>
                        {depTask.id} • {depTask.status}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRemoveDependency(id)}
                      className="h-8 w-8 p-0"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}

              {/* Search and add new dependencies */}
              <div className="pt-2">
                <Input
                  placeholder="Search tasks to add..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="mb-2"
                />
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {availableTasks.slice(0, 10).map((t) => (
                    <div
                      key={t.id}
                      className="cursor-pointer rounded p-2 hover:bg-muted"
                      onClick={() => handleAddDependency(t.id)}
                    >
                      <Plus className="h-4 w-4 text-muted-foreground" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{t.title}</div>
                        <div className="text-xs text-muted-foreground">{t.id}</div>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {t.status}
                      </Badge>
                    </div>
                  ))}
                  {availableTasks.length === 0 && (
                    <p className={`py-2 text-sm italic ${WIDGET_MUTED_TEXT_CLASS}`}>No tasks found</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Save/Cancel buttons */}
        {isEditing && (
          <div className="flex gap-2 pt-2 border-t">
            <Button size="sm" onClick={handleSave} className="flex-1">
              <Check className="w-4 h-4 mr-1" />
              Save
            </Button>
            <Button size="sm" variant="outline" onClick={handleCancel} className="flex-1">
              <X className="w-4 h-4 mr-1" />
              Cancel
            </Button>
          </div>
        )}

        {/* Blocked Tasks (tasks that depend on this one) */}
        {blockedTasks.length > 0 && (
          <div className="pt-4 border-t">
            <h4 className="mb-2 flex items-center gap-1 text-sm font-semibold text-foreground">
              <ArrowRight className={`h-4 w-4 ${WIDGET_ALERT_INFO_TEXT_CLASS}`} />
              Blocks ({blockedTasks.length})
            </h4>
            <div className="space-y-2">
              {blockedTasks.map((blockedTask) => (
                <DependencyTaskCard key={blockedTask.id} task={blockedTask} direction="blocked" />
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DependencyTaskCard({ task, direction }: { task: Task; direction: 'blocking' | 'blocked' }) {
  const isComplete = task.status === 'Done' || task.status === 'Completed';
  const isBlocked = task.status === 'Blocked';

  return (
    <div
      className={`rounded-lg p-3 ${
        direction === 'blocking'
          ? WIDGET_ALERT_WARNING_CLASS
          : WIDGET_ALERT_INFO_CLASS
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="outline" className="font-mono text-xs">
              {task.id}
            </Badge>
            <Badge className={`border text-xs ${getPriorityColors(task.priority).badge}`}>{task.priority}</Badge>
            {isComplete && <Check className={`h-4 w-4 ${getStatusColors('Completed').text}`} />}
            {isBlocked && <AlertCircle className={`h-4 w-4 ${getStatusColors('Blocked').text}`} />}
          </div>
          <h5 className="line-clamp-2 text-sm font-medium text-foreground">
            {task.title}
          </h5>
          <div className={`mt-1 flex items-center gap-2 text-xs ${WIDGET_MUTED_TEXT_CLASS}`}>
            <span
              className={`rounded border px-2 py-0.5 ${
                isComplete
                  ? getStatusColors('Completed').badge
                  : isBlocked
                  ? getStatusColors('Blocked').badge
                  : getStatusColors(task.status).badge
              }`}
            >
              {task.status}
            </span>
            {task.estimatedHours && <span>{task.estimatedHours}h est.</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
